import { atom, computed } from 'nanostores';
import { $accessToken } from '~/lib/stores/auth';
import { createScopedLogger } from '~/utils/logger';

const logger = createScopedLogger('TurnQueue');

/**
 * Client state for ai's build waiting room.
 *
 * When every turn slot is busy the server parks the request (202 AI_QUEUED)
 * rather than failing it. This store holds the resulting place in line and,
 * critically, keeps it CURRENT without the user doing anything.
 *
 * Two independent update channels, on purpose:
 *
 * 1. **Websocket push** (`sandbox:queue-update` / `sandbox:queue-admitted`) —
 *    instant, and how the number normally moves. Wired in SandboxRuntime,
 *    which already owns the socket and the chat room.
 *
 * 2. **Poll** (`GET /api/chat/:id/queue`, every 5s) — the fallback, and also
 *    the server's liveness signal: each poll refreshes a short TTL on the
 *    queue entry. Stop polling and the place is released automatically.
 *
 * The redundancy is the point. If the socket drops, positions keep updating
 * from the poll; if the poll fails once, the socket keeps them live. Both read
 * the same server-side state, so they cannot disagree — and because that state
 * lives on the server keyed by chatId, a page reload rejoins the same position
 * instead of losing its place. Refreshing is safe, never required.
 */

export interface TurnQueueState {
  chatId: string | null;
  /** 1-based place in line. */
  position: number;
  /** Total waiting, including this client. */
  total: number;
  /** Server's best-effort seconds until start. */
  etaSec: number;
  /** True between being told to start and the send actually going out. */
  starting: boolean;
}

const IDLE: TurnQueueState = {
  chatId: null,
  position: 0,
  total: 0,
  etaSec: 0,
  starting: false,
};

/** Poll cadence. Also the heartbeat rate — the server's entry TTL (20s) is set
 *  to tolerate three consecutive misses, so a brief network blip never costs
 *  someone their place. */
const POLL_INTERVAL_MS = 5_000;

export const $turnQueue = atom<TurnQueueState>(IDLE);

export const $isQueued = computed($turnQueue, (s) => s.chatId !== null);

let pollTimer: ReturnType<typeof setInterval> | null = null;

/**
 * Called when the queue says it's this client's turn.
 *
 * Set by Chat.client so the store can trigger the actual re-send without
 * importing chat internals (which would be a cycle). A ref rather than an
 * event bus because there is exactly one active chat at a time.
 */
let onAdmitted: (() => void) | null = null;

export function setQueueAdmitHandler(fn: (() => void) | null) {
  onAdmitted = fn;
}

function authHeaders(): HeadersInit {
  const token = $accessToken.get();

  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** Enter the waiting room and start keeping the position fresh. */
export function enterQueue(chatId: string, ticket: { position: number; total: number; etaSec: number }) {
  $turnQueue.set({
    chatId,
    position: ticket.position,
    total: ticket.total,
    etaSec: ticket.etaSec,
    starting: false,
  });

  startPolling(chatId);
  logger.info(`Queued for chat ${chatId} at position ${ticket.position}/${ticket.total}`);
}

/**
 * Apply a websocket position update.
 *
 * Guarded on chatId: broadcasts carry the whole line, and a stale frame for a
 * chat the user has since navigated away from must not repaint the queue for
 * the chat they're now looking at.
 */
export function applyQueueUpdate(ticket: { chatId: string; position: number; total: number; etaSec: number }) {
  const current = $turnQueue.get();

  if (current.chatId !== ticket.chatId || current.starting) {
    return;
  }

  $turnQueue.set({ ...current, position: ticket.position, total: ticket.total, etaSec: ticket.etaSec });
}

/**
 * The server admitted this chat — start the build.
 *
 * `starting` latches before the handler runs so a second admit signal can't
 * fire the send twice. Both channels can legitimately observe the same
 * admission — the websocket event and a poll that comes back 204 — and starting
 * twice would mean two builds and two charges. The server's duplicate-turn
 * guard would catch it, but relying on that is not a design.
 *
 * The latch is then dropped along with the rest of the state: this whole
 * function is synchronous, so nothing renders between the two writes. The store
 * goes straight from queued to idle, and the build's own streaming UI takes
 * over from there.
 */
export function admitFromQueue(chatId: string) {
  const current = $turnQueue.get();

  if (current.chatId !== chatId || current.starting) {
    return;
  }

  $turnQueue.set({ ...current, starting: true });
  stopPolling();
  logger.info(`Admitted from queue for chat ${chatId} — starting build`);

  try {
    onAdmitted?.();
  } catch (err) {
    logger.error('queue admit handler threw', err);
  }

  $turnQueue.set(IDLE);
}

/**
 * Leave the line deliberately (Stop pressed, chat switched, unmount).
 *
 * Best-effort: the server's TTL reclaims an abandoned entry within ~20s
 * regardless, so a failed request here costs a slightly stale count for
 * everyone behind, not correctness.
 */
export function leaveQueue(chatId?: string) {
  const id = chatId ?? $turnQueue.get().chatId;

  stopPolling();
  $turnQueue.set(IDLE);

  if (!id) {
    return;
  }

  void fetch(`/api/chat/${id}/queue/leave`, {
    method: 'POST',
    credentials: 'include',
    headers: authHeaders(),
  }).catch(() => {
    // TTL cleanup covers this.
  });
}

function startPolling(chatId: string) {
  stopPolling();

  pollTimer = setInterval(() => void pollOnce(chatId), POLL_INTERVAL_MS);
}

function stopPolling() {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
}

/**
 * One heartbeat + position read.
 *
 * A 204 means the server no longer has us queued. That has exactly two causes:
 * we were admitted (and the websocket admit event was missed — a dropped socket
 * is precisely when this fallback matters), or the entry lapsed. Both resolve
 * to "start the build": being admitted and never starting would strand the user
 * forever holding a granted slot, and a lapsed entry re-queues harmlessly if
 * the server is still full. Failing open is the safe direction here.
 */
async function pollOnce(chatId: string) {
  const current = $turnQueue.get();

  if (current.chatId !== chatId || current.starting) {
    stopPolling();

    return;
  }

  try {
    const res = await fetch(`/api/chat/${chatId}/queue`, {
      credentials: 'include',
      headers: authHeaders(),
    });

    if (res.status === 204) {
      admitFromQueue(chatId);

      return;
    }

    if (!res.ok) {
      return;
    }

    const ticket = (await res.json()) as { position: number; total: number; etaSec: number };

    applyQueueUpdate({ chatId, ...ticket });
  } catch {
    // Transient — the next tick retries, and the server TTL tolerates
    // several consecutive misses before dropping our place.
  }
}

/** Test seam: reset module state between specs. */
export function __resetTurnQueue() {
  stopPolling();
  $turnQueue.set(IDLE);
  onAdmitted = null;
}
